import React, { useContext } from 'react';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import 'bulma/css/bulma.min.css';

import { SampleConfigurationSet } from '../sampleSets.ts'
import { BeamlineContext } from '../beamlineProvider.tsx'
import { SampleSetsContext } from '../sampleSetsProvider.tsx';
import { InputAutocomplete, SearchFunctions, SearchResult, SelectingStatus } from './inputAutocomplete.tsx'
import { highlightSearchTermsInString } from "../utils.tsx";
import './setAutocomplete.css';


interface SetSearchFunctions {
  itemSelected: (item: SampleConfigurationSet) => SelectingStatus;
  selectedNone: () => SelectingStatus;
}


// Settings passed in with the React component
interface SetAutocompleteParameters {
  selectedItem: SampleConfigurationSet | null;
  searchFunctions: SetSearchFunctions;
  allowedSets?: string[];
  inputSize?: string;
  iconSize?: SizeProp;
  isReadOnly?: boolean;
  greenWhenValid?: boolean;
  canSelectNone?: boolean;
  showClearButton?: boolean;
  triggerFocus?: boolean;
  resetOnDefocus?: boolean;
}


function SetAutocomplete(settings:SetAutocompleteParameters) {

  const beamlineContext = useContext(BeamlineContext);
  const setsContext = useContext(SampleSetsContext);

  async function searchSets(searchString: string): Promise<SearchResult<SampleConfigurationSet>> {
    const searchStringLower = searchString.toLowerCase();
    // If allowedSets is an array, we use it even if it's empty.
    const slugsToTest = Array.isArray(settings.allowedSets) ? settings.allowedSets : setsContext.sets.getSortedSets().map((s) => s.slug);
    const toTest = slugsToTest.map((slug) => setsContext.sets.getBySlug(slug)!);
    const matches = toTest.filter((p) =>
                      p.name.toLowerCase().includes(searchStringLower) ||
                      p.description.toLowerCase().includes(searchStringLower));
    return { matches: matches };
  };

  function renderMatch(item: SampleConfigurationSet, searchString: string): React.JSX.Element {
    return (<>
              <td>{ highlightSearchTermsInString(item.name, [searchString]) }</td>
              <td>{ highlightSearchTermsInString(item.description, [searchString]) }</td>
            </>);
  }

  const setSearchFunctions: SearchFunctions<SampleConfigurationSet> = {
    itemToString: (item) => { return item.name },
    itemsRetriever: searchSets,
    itemSelected: settings.searchFunctions.itemSelected,
    selectedNone: settings.searchFunctions.selectedNone,
    renderMatchedItem: renderMatch
  };

  return InputAutocomplete<SampleConfigurationSet>({
      value: settings.selectedItem ? settings.selectedItem.name : "",
      selectedItem: settings.selectedItem,
      searchFunctions: setSearchFunctions,
      addedClass: "set",
      inputSize: settings.inputSize,
      iconSize: settings.iconSize,
      placeholder: "Select a " + beamlineContext.setNoun,
      isReadOnly: settings.isReadOnly,
      greenWhenValid: settings.greenWhenValid,
      canSelectNone: settings.canSelectNone,
      showClearButton: settings.showClearButton,
      resetOnDefocus: settings.resetOnDefocus,
      triggerFocus: settings.triggerFocus,
      showAllByDefault: true,
      renderResultsAsTable: true
    });
}

export { SetAutocomplete }
export type { SetSearchFunctions }
